<script>
  import { format } from 'svelte-i18n';
  import { Button, Tooltip } from '@sveltestrap/sveltestrap';

  import { KeyValue } from '@client/components/custom-fields/key-value';
  import { Icon } from '@client/components/icon';
  import { Input } from '@client/components/input';
  import { LockAllToggle } from '@client/components/lock-all-toggle';

  export let fields = [];
  export let framed = true;
  export let editing = false;

  export const FIELD_TYPES = {
    select: {
      type: 'select',
      label: 'Dropdown',
      value: 'select'
    },
    text: {
      type: 'text',
      label: 'Text',
      value: 'text'
    },
    boolean: {
      type: 'boolean',
      label: 'Checkbox',
      value: 'boolean'
    }
  };

  const toggleFieldValue = (index, field) => {
    if (editing) {
      fields[index][field] = !fields[index][field];
      fields = [...fields];

      return true;
    }

    return false;
  };

  const addField = () => {
    fields.push({
      label: '',
      name: '',
      value: '',
      values: [],
      type: 'select',
      persist: false
    });

    fields = [...fields];
  };

  const onRemoveField = (index) => {
    fields.splice(index, 1);
    fields = [...fields];
  };

  const toggleAllLocks = () => {
    if (!editing) return;
    const next = !allLocked;
    fields.forEach((field) => (field.persist = next));
    fields = [...fields];
  };

  $: allLocked = fields.length > 0 && fields.every((field) => field.persist);
</script>

<div class="fields" class:framed class:readonly={!editing}>
  <div class="headers">
    {#if editing && fields.length}
      <LockAllToggle
        active={allLocked}
        tooltipActive="label.UNLOCK_ALL_FIELDS"
        tooltipInactive="label.LOCK_ALL_FIELDS"
        id="custom-fields-lock-all"
        on:toggle={toggleAllLocks}
      />
    {:else}
      <div>&nbsp;</div>
    {/if}
    <div class="field">{$format('label.FIELD_TYPE')}</div>
    <div class="field">
      {$format('label.FIELD_LABEL')}
      <Icon name="help-circle" id="custom-field-labels" />
      <Tooltip target="custom-field-labels" placement="top">
        {@html $format('label.TOOLTIP.FIELD_LABEL')}
      </Tooltip>
    </div>
    <div class="field">
      {$format('label.FIELD_VALUE')}
      <Icon name="help-circle" id="custom-field-values" />
      <Tooltip target="custom-field-values" placement="top">
        {@html $format('label.TOOLTIP.FIELD_VALUE')}
      </Tooltip>
    </div>
  </div>

  {#each fields as field, fieldIndex}
    <div class="field-group">
      <div class="columns">
        <Icon
          name={field.persist ? 'lock' : 'lock-open'}
          id="param-field-{fieldIndex}-lock"
          color={field.persist ? 'danger' : undefined}
          class={editing ? 'pointer' : ''}
          on:click={() => toggleFieldValue(fieldIndex, 'persist')}
          role="button"
          tabindex="0"
        />
        <Tooltip target="param-field-{fieldIndex}-lock" placement="top">
          {#if field.persist}
            {$format('label.TOOLTIP.UI_HELPERS_FIELD_PERSIST')}
          {:else}
            {$format('label.TOOLTIP.UI_HELPERS_FIELD_RESET')}
          {/if}
        </Tooltip>
        {#if editing}
          <select class="field" type="text" bind:value={field.type}>
            {#each Object.keys(FIELD_TYPES) as which}
              <option value={FIELD_TYPES[which].type}>{FIELD_TYPES[which].label}</option>
            {/each}
          </select>
          <Input type="text" class="raw field" bind:value={field.label} placeholder={$format('label.LABEL')} />
          <Input type="text" class="raw field" bind:value={field.name} placeholder={$format('label.VALUE')} />
          <Icon name="x-circle" tabindex="0" role="button" on:click={() => onRemoveField(fieldIndex)} />
        {:else}
          <div class="field">{FIELD_TYPES[field.type].label}</div>
          <div class="field">{field.label}</div>
          <div class="field">{field.name}</div>
        {/if}
      </div>

      {#if field.type === 'select'}
        <div class="columns select-options" class:readonly={!editing}>
          <div>&nbsp;</div>
          <div>&nbsp;</div>
          <div class="key-value">
            <KeyValue
              addButtonLabel={$format('label.ADD_OPTION')}
              keyColumnLabel={$format('label.LABEL')}
              keyProperty="label"
              keyPlaceholder={$format('label.OPTION_LABEL')}
              valuePlaceholder={$format('label.OPTION_VALUE')}
              bind:values={field.values}
              bind:editing
            />
          </div>
        </div>
      {/if}
    </div>
  {/each}

  {#if editing}
    <Button color="secondary" size="sm" class="pointer {fields.length ? 'mt-3' : ''}" on:click={addField}>
      <Icon name="plus" />
      <span>{$format('label.FIELD')}</span>
    </Button>
  {/if}
</div>

<style lang="scss" src="./custom-fields.scss"></style>
